// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Checkbox,
  Divider,
  Grid,
  Group,
  Modal,
  MultiSelect,
  Radio,
  Select,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { DateInput, TimeInput } from '@mantine/dates';
import { useDisclosure } from '@mantine/hooks';
import { formatDate } from '@medplum/core';
import type {
  Appointment,
  ChargeItem,
  Claim,
  Encounter,
  MedicationRequest,
  Patient,
  Practitioner,
  ServiceRequest,
} from '@medplum/fhirtypes';
import { CodingInput, useMedplum } from '@medplum/react';
import * as Sentry from '@sentry/react';
import {
  IconExternalLink,
  IconFilePlus,
  IconLock,
  IconPlus,
  IconSignature,
  IconTrash,
  IconVideo,
  IconWriting,
  IconX,
} from '@tabler/icons-react';
import type { Dispatch, JSX, SetStateAction } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { MEDICATION_FREQUENCY_OPTIONS } from '../../constants/medical-info';
import type { ChecklistItem } from '../../hooks/useEncounterChart';
import type { EncounterNoteController } from '../../hooks/useEncounterNoteController';
import { useRole } from '../../hooks/useRole';
import { ChartNoteStatus } from '../../types/encounter';
import { buildIcd10Concept, ICD10_VALUESET } from '../../utils/icd10';
import { showErrorNotification } from '../../utils/notifications';
import {
  createHeightObservation,
  createWeightObservation,
  lbsToKg,
  parseHeightImperialToCm,
  parseWeightLbs,
} from '../../utils/patient-vitals';
import { hasPermission } from '../../utils/permissions';
import { isReferralMedicationRequest, isReferralServiceRequest } from '../../utils/referral-pdf';
import { getTelehealthInfo } from '../../utils/telehealth';
import { PatientTreatmentsSection } from '../patient/PatientTreatmentsSection';
import { usePatientVitals } from '../patient/PatientVitalsSummary';
import { ScriptSureIFrame } from '../prescribing/ScriptSureIFrame';
import { BillingTab } from './BillingTab';
import { DiagnosisCodeInput } from './DiagnosisCodeInput';
import styles from './EncounterNoteRefactoredShell.module.css';
import { EncounterWorkspaceShell } from './EncounterWorkspaceShell';
import { MIN_DIAGNOSIS_ENTRIES } from './encounter-chart-types';
import { readReferenceChecklist } from './encounter-note-checklist';
import { NewPrescriptionModal } from './NewPrescriptionModal';
import { OrderNewTestModal } from './OrderNewTestModal';
import { ReferralModal, type ReferralNoteDiagnosis } from './ReferralModal';
import { SignLockDialog } from './SignLockDialog';
import { SupplementalInfoSection } from './SupplementalInfoSection';

const BRAND = '#640040';
const OTHER_FREQUENCY_VALUE = 'other';
const REFERENCE_CHECKLIST_ITEMS = [
  {
    lead: 'Begin appointment by ',
    emphasis: 'confirming name and birthdate',
    tail: ' to avoid PHI disclosure.',
  },
  {
    lead: "Confirm the patient's ",
    emphasis: 'current pharmacy and shipping address',
    tail: ' before placing orders.',
  },
  {
    lead: 'Review ',
    emphasis: 'intake questionnaire responses',
    tail: ' and reconcile against the medication list.',
  },
];

interface EncounterNoteRefactoredShellProps {
  appointment: Appointment | undefined;
  canConduct: boolean;
  canSchedule: boolean;
  chargeItems: ChargeItem[];
  checklistItems: ChecklistItem[];
  claim: Claim | undefined;
  controller: EncounterNoteController;
  encounter: Encounter;
  onConclude: (() => void) | undefined;
  onOpenSchedule: (() => void) | undefined;
  patient: Patient;
  practitioner: Practitioner | undefined;
  setChargeItems: Dispatch<SetStateAction<ChargeItem[]>>;
  setClaim: Dispatch<SetStateAction<Claim | undefined>>;
  setEncounter: Dispatch<SetStateAction<Encounter | undefined>>;
  setPractitioner: Dispatch<SetStateAction<Practitioner | undefined>>;
}

function getStatusTone(chartNoteStatus: EncounterNoteController['chartNoteStatus']): {
  label: string;
  tone: 'locked' | 'signed' | 'draft';
} {
  if (chartNoteStatus === ChartNoteStatus.SignedAndLocked) {
    return { label: 'Signed & Locked', tone: 'locked' };
  }
  if (chartNoteStatus === ChartNoteStatus.Signed) {
    return { label: 'Signed', tone: 'signed' };
  }
  return { label: 'Draft', tone: 'draft' };
}

function getOrderLabel(medicationRequest: MedicationRequest): string {
  return (
    medicationRequest.medicationCodeableConcept?.text ||
    medicationRequest.medicationCodeableConcept?.coding?.[0]?.display ||
    'Therapy'
  );
}

function referralNoteValue(request: ServiceRequest, prefix: string): string {
  return (
    request.note
      ?.find((note) => note.text?.startsWith(prefix))
      ?.text?.slice(prefix.length)
      .trim() ?? ''
  );
}

function getDiagnosisInputKey(
  id: string | undefined,
  index: number,
  code: string | undefined,
  display: string | undefined
): string {
  return `${id ?? `new:${index}`}:${code ?? ''}:${display ?? ''}`;
}
function isRpmOrder(medicationRequest: MedicationRequest): boolean {
  return medicationRequest.medicationCodeableConcept?.coding?.some((coding) => coding.code === 'rpm') ?? false;
}

function isDmeOrder(medicationRequest: MedicationRequest): boolean {
  return (
    medicationRequest.medicationCodeableConcept?.coding?.some(
      (coding) => coding.system === 'http://polarissleep.com/therapies'
    ) ?? false
  );
}

function openPrescriptionWindow(
  navigate: ReturnType<typeof useNavigate>,
  patientId: string | undefined,
  encounterId: string | undefined,
  medicationRequest: MedicationRequest
): void {
  if (!patientId || !medicationRequest.id) {
    return;
  }
  const route = isRpmOrder(medicationRequest) ? 'RpmOrder' : 'DmeOrder';
  const encounterQuery = encounterId ? `&encounterId=${encounterId}` : '';
  navigate(`/${route}/${patientId}?prescriptionId=${medicationRequest.id}${encounterQuery}`);
}

function openRpmOrderWindow(
  navigate: ReturnType<typeof useNavigate>,
  patientId: string | undefined,
  encounterId: string | undefined,
  prescriptionId?: string
): void {
  if (!patientId || !encounterId) {
    return;
  }
  navigate(`/RpmOrder/${patientId}?prescriptionId=${prescriptionId ?? ''}&encounterId=${encounterId}`);
}

function getDiagnosisLabel(index: number): string {
  if (index === 0) {
    return 'Primary Diagnosis';
  }
  if (index === 1) {
    return 'Additional Diagnosis';
  }
  return `Additional Diagnosis ${index}`;
}

function calculateBmi(height: string, weight: string): string {
  const match = height.match(/^(\d+)\s*[′'’]\s*(\d+)/);
  const weightValue = Number(weight);
  if (!match || !Number.isFinite(weightValue) || weightValue <= 0) {
    return '';
  }
  const heightInches = Number(match[1]) * 12 + Number(match[2]);
  return ((weightValue / (heightInches * heightInches)) * 703).toFixed(1);
}

export function EncounterNoteRefactoredShell(props: EncounterNoteRefactoredShellProps): JSX.Element {
  const {
    appointment,
    canConduct,
    canSchedule,
    chargeItems,
    checklistItems,
    claim,
    controller,
    encounter,
    onConclude,
    onOpenSchedule,
    patient,
    practitioner,
    setChargeItems,
    setClaim,
    setEncounter,
    setPractitioner,
  } = props;
  const medplum = useMedplum();
  const navigate = useNavigate();
  const { vitals: profileVitals } = usePatientVitals(patient);
  const {
    chartNoteStatus,
    confirmClinicalRow,
    form,
    addendums,
    provenances,
    refreshOrders,
    serviceRequests,
    medicationRequests,
    saveDraft,
    sign,
    addAddendum,
    toggleTask,
    isLocked,
  } = controller;
  const [signOpened, { open: openSign, close: closeSign }] = useDisclosure(false);
  const [newTestOpened, { open: openNewTest, close: closeNewTest }] = useDisclosure(false);
  const [referralOpened, { open: openReferral, close: closeReferral }] = useDisclosure(false);
  const { role, isAdmin } = useRole();
  // UX gate only; the MedicationRequest write in the role's AccessPolicy is the security boundary.
  const canCreateReferral = hasPermission({ role, isAdmin, permission: 'create-referral-order' });
  const [newPrescriptionOpened, { open: openNewPrescription, close: closeNewPrescription }] = useDisclosure(false);
  const [scriptSureOpened, { open: openScriptSure, close: closeScriptSure }] = useDisclosure(false);
  const [selectedTest, setSelectedTest] = useState<ServiceRequest | undefined>();
  const [addendumText, setAddendumText] = useState('');
  const [referenceChecklist, setReferenceChecklist] = useState<boolean[]>(() =>
    readReferenceChecklist(encounter, REFERENCE_CHECKLIST_ITEMS.length)
  );
  const [checklistHidden, setChecklistHidden] = useState(false);
  const [vitals, setVitals] = useState({
    height: '',
    weight: '',
    bloodPressure: '',
    heartRate: '',
    respiratoryRate: '',
  });
  const statusTone = getStatusTone(chartNoteStatus);
  const bmi = calculateBmi(vitals.height, vitals.weight);
  const checklistStateToSave = checklistItems.length === 0 ? referenceChecklist : undefined;
  const referralRequests = medicationRequests.filter(isReferralMedicationRequest);
  const therapyRequests = medicationRequests.filter((request) => !isReferralMedicationRequest(request));
  // Referrals created before SLE-727 were ServiceRequests; keep them visible on the note.
  const legacyReferralRequests = serviceRequests.filter(isReferralServiceRequest);

  useEffect(() => {
    const noteVitals = form.values.vitalSigns;
    if (!noteVitals) {
      return;
    }
    const readValue = (label: string): string =>
      noteVitals.match(new RegExp(`^${label}:\\s*(.+)$`, 'im'))?.[1]?.trim() ?? '';
    setVitals((current) => ({
      ...current,
      bloodPressure: current.bloodPressure || readValue('Blood Pressure'),
      heartRate: current.heartRate || readValue('Heart Rate'),
      respiratoryRate: current.respiratoryRate || readValue('Respiratory Rate'),
    }));
  }, [form.values.vitalSigns]);

  useEffect(() => {
    setVitals((current) => ({
      ...current,
      height:
        current.height ||
        (profileVitals.heightCm
          ? `${Math.floor(profileVitals.heightCm / 30.48)}'${Math.round((profileVitals.heightCm / 2.54) % 12)}"`
          : ''),
      weight: current.weight || (profileVitals.weightKg ? String(Math.round(profileVitals.weightKg / 0.453592)) : ''),
    }));
  }, [profileVitals.heightCm, profileVitals.weightKg]);

  const saveVitals = async (): Promise<string> => {
    if (!patient.id) {
      return '';
    }
    const heightCm = parseHeightImperialToCm(vitals.height);
    const weightLbs = parseWeightLbs(vitals.weight);
    const vitalSigns = [
      heightCm !== undefined ? `Height: ${heightCm} cm` : '',
      weightLbs !== undefined ? `Weight: ${lbsToKg(weightLbs)} kg` : '',
      vitals.bloodPressure ? `Blood Pressure: ${vitals.bloodPressure}` : '',
      vitals.heartRate ? `Heart Rate: ${vitals.heartRate}` : '',
      vitals.respiratoryRate ? `Respiratory Rate: ${vitals.respiratoryRate}` : '',
    ]
      .filter(Boolean)
      .join('\n');
    form.setFieldValue('vitalSigns', vitalSigns);
    if (heightCm !== undefined && heightCm !== profileVitals.heightCm) {
      await createHeightObservation(medplum, patient, heightCm);
    }
    if (weightLbs !== undefined && lbsToKg(weightLbs) !== profileVitals.weightKg) {
      await createWeightObservation(medplum, patient, lbsToKg(weightLbs));
    }
    return vitalSigns;
  };

  const medicationOptions = useMemo(
    () =>
      form.values.medications.map((row, index) => ({
        value: row.clientId,
        label: row.medication || `Medication ${index + 1}`,
      })),
    [form.values.medications]
  );

  // Pass each diagnosis's real coding system so hydrated non-ICD-10 diagnoses are not relabelled as ICD-10.
  const referralNoteDiagnoses = useMemo<ReferralNoteDiagnosis[]>(
    () =>
      form.values.diagnoses.map((diagnosis) => ({
        code: diagnosis.code,
        text: diagnosis.text,
        system: diagnosis.concept?.coding?.find((coding) => coding.code === diagnosis.code)?.system,
      })),
    [form.values.diagnoses]
  );

  const handleOpenSign = async (): Promise<void> => {
    try {
      const vitalSigns = await saveVitals();
      await saveDraft(vitalSigns, checklistStateToSave);
      openSign();
    } catch {
      // saveDraft already reports the error
    }
  };

  const handleSaveDraft = async (): Promise<void> => {
    const vitalSigns = await saveVitals();
    await saveDraft(vitalSigns, checklistStateToSave);
  };

  const handleAddAddendum = async (): Promise<void> => {
    try {
      await addAddendum(addendumText.trim());
      setAddendumText('');
    } catch (error) {
      showErrorNotification(error);
    }
  };

  const handleCloseNewTest = (): void => {
    closeNewTest();
    refreshOrders().catch(showErrorNotification);
  };

  const handleCloseReferral = (): void => {
    closeReferral();
    refreshOrders().catch(showErrorNotification);
  };

  const joinUrl = appointment ? getTelehealthInfo(appointment)?.joinUrl : undefined;

  const actionButtons = (
    <Group gap="sm">
      <Button
        component={canConduct && joinUrl ? 'a' : 'button'}
        className={styles.noteHeaderGhostButton}
        href={canConduct ? joinUrl : undefined}
        target="_blank"
        rel="noopener noreferrer"
        leftSection={<IconVideo size={16} />}
        disabled={!joinUrl || !canConduct}
      >
        Join
      </Button>
      <Button className={styles.noteHeaderGhostButton} leftSection={<IconTrash size={16} />} disabled>
        Delete Note
      </Button>
      <Button
        className={styles.noteHeaderGhostButton}
        disabled={isLocked}
        onClick={() => handleSaveDraft().catch(() => undefined)}
      >
        Save as Draft
      </Button>
      <Button
        className={styles.noteHeaderPrimaryButton}
        disabled={isLocked}
        onClick={() => handleOpenSign().catch(() => undefined)}
      >
        Sign & Lock
      </Button>
    </Group>
  );

  return (
    <>
      <EncounterWorkspaceShell
        appointment={appointment}
        canSchedule={canSchedule}
        currentTab="encounter-note"
        encounter={encounter}
        onConclude={onConclude}
        onOpenSchedule={onOpenSchedule}
        patient={patient}
      >
        <Card className={`${styles.card} ${isLocked ? styles.lockedNote : ''}`} p={0}>
          <div className={styles.noteHeader}>
            <span className={styles.srOnly}>Billing</span>
            <div className={styles.noteHeaderTitleRow}>
              <Title aria-label="Encounter Note" c="white" order={2}>
                Encounter Note <span className={styles.noteVersion}>(v.1)</span>
              </Title>
              <span className={`${styles.noteStatusPill} ${styles[`noteStatusPill_${statusTone.tone}`]}`}>
                {statusTone.label}
              </span>
            </div>
            {actionButtons}
          </div>

          <Stack gap="lg" p="xl">
            <SupplementalInfoSection patient={patient} encounter={encounter} appointment={appointment} />

            <Card withBorder radius="lg" p="lg">
              <Stack gap="md">
                <div className={styles.sectionHeader}>
                  <Title order={3}>Checklist</Title>
                  <Button variant="subtle" size="compact-sm" onClick={() => setChecklistHidden((hidden) => !hidden)}>
                    {checklistHidden ? 'Show' : 'Hide'}
                  </Button>
                </div>
                {!checklistHidden &&
                  (checklistItems.length === 0 ? (
                    <Stack gap="sm">
                      {REFERENCE_CHECKLIST_ITEMS.map((item, index) => (
                        <Checkbox
                          key={item.emphasis}
                          checked={referenceChecklist[index]}
                          disabled={isLocked}
                          label={
                            <span>
                              {item.lead}
                              <strong>{item.emphasis}</strong>
                              {item.tail}
                            </span>
                          }
                          onChange={(event) => {
                            const next = [...referenceChecklist];
                            next[index] = event.currentTarget.checked;
                            setReferenceChecklist(next);
                          }}
                        />
                      ))}
                    </Stack>
                  ) : (
                    <Stack gap="sm">
                      {checklistItems.map((item) => (
                        <Checkbox
                          key={item.id}
                          checked={
                            item.task
                              ? ['completed', 'cancelled', 'failed', 'rejected', 'entered-in-error'].includes(
                                  item.task.status
                                )
                              : false
                          }
                          disabled={isLocked || !item.task}
                          label={item.label}
                          onChange={() => item.task && toggleTask(item.task).catch(showErrorNotification)}
                        />
                      ))}
                    </Stack>
                  ))}
              </Stack>
            </Card>

            <Card withBorder radius="lg" p="lg">
              <Stack gap="lg">
                <div className={styles.sectionHeader}>
                  <Stack gap={2}>
                    <Title order={3}>Patient Condition</Title>
                  </Stack>
                </div>

                <Textarea
                  autosize
                  disabled={isLocked}
                  label="Chief Complaint(s)"
                  minRows={2}
                  placeholder="Document the reason for today's visit"
                  {...form.getInputProps('visitReason')}
                />

                <Textarea
                  autosize
                  disabled={isLocked}
                  label="HPI"
                  minRows={3}
                  placeholder="Document the history of present illness"
                  {...form.getInputProps('hpi')}
                />

                <Textarea
                  autosize
                  disabled={isLocked}
                  label="Review of Systems (ROS)"
                  minRows={3}
                  placeholder="Document review of systems"
                  {...form.getInputProps('reviewSymptoms')}
                />

                <Textarea
                  autosize
                  disabled={isLocked}
                  label="Exam"
                  minRows={4}
                  placeholder="Document physical examination findings"
                  {...form.getInputProps('exam')}
                />

                <div className={styles.conditionSubhead}>Existing Conditions</div>
                <Table.ScrollContainer className={styles.conditionRows} minWidth={0}>
                  <Table className={styles.dataTable} verticalSpacing="md">
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th className={styles.tableCell}>Condition</Table.Th>
                        <Table.Th className={styles.tableCell}>Code</Table.Th>
                        <Table.Th className={styles.tableCell}>Onset Date</Table.Th>
                        <Table.Th className={styles.tableCell}>Medication(s)</Table.Th>
                        <Table.Th />
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {form.values.conditions.length === 0 ? (
                        <Table.Tr>
                          <Table.Td colSpan={5}>
                            <Text c="dimmed">No conditions added yet.</Text>
                          </Table.Td>
                        </Table.Tr>
                      ) : (
                        form.values.conditions.map((row, index) => (
                          <Table.Tr key={row.clientId}>
                            <Table.Td>
                              <TextInput
                                disabled={isLocked}
                                placeholder="Condition"
                                value={row.condition}
                                onChange={(event) =>
                                  form.setFieldValue(`conditions.${index}.condition`, event.currentTarget.value)
                                }
                              />
                            </Table.Td>
                            <Table.Td>
                              <TextInput
                                disabled={isLocked}
                                placeholder="Code"
                                value={row.code}
                                onChange={(event) =>
                                  form.setFieldValue(`conditions.${index}.code`, event.currentTarget.value)
                                }
                              />
                            </Table.Td>
                            <Table.Td>
                              <TextInput
                                disabled={isLocked}
                                placeholder="YYYY-MM-DD"
                                type="date"
                                value={row.onsetDate ?? ''}
                                onChange={(event) =>
                                  form.setFieldValue(`conditions.${index}.onsetDate`, event.currentTarget.value || null)
                                }
                              />
                            </Table.Td>
                            <Table.Td>
                              <MultiSelect
                                data={medicationOptions}
                                disabled={isLocked}
                                placeholder="Link medications"
                                value={row.medicationIds}
                                onChange={(value) => form.setFieldValue(`conditions.${index}.medicationIds`, value)}
                              />
                            </Table.Td>
                            <Table.Td>
                              <Stack gap={2}>
                                <Text size="xs" c="dimmed">
                                  Source: {row.sourceDate ? formatDate(row.sourceDate) : 'chart'}
                                </Text>
                                {!row.confirmedAt && row.id ? (
                                  <Button
                                    size="compact-xs"
                                    variant="light"
                                    disabled={isLocked}
                                    onClick={() =>
                                      confirmClinicalRow('Condition', row.id as string).catch(showErrorNotification)
                                    }
                                  >
                                    Still current
                                  </Button>
                                ) : (
                                  <Badge size="xs" color="green">
                                    Confirmed
                                  </Badge>
                                )}
                              </Stack>
                            </Table.Td>
                            <Table.Td>
                              <ActionIcon
                                color="red"
                                disabled={isLocked}
                                onClick={() => form.removeListItem('conditions', index)}
                              >
                                <IconTrash size={16} />
                              </ActionIcon>
                            </Table.Td>
                          </Table.Tr>
                        ))
                      )}
                    </Table.Tbody>
                  </Table>
                </Table.ScrollContainer>

                <Button
                  className={styles.inlineAdd}
                  variant="subtle"
                  leftSection={<IconPlus size={16} />}
                  disabled={isLocked}
                  onClick={() =>
                    form.insertListItem('conditions', {
                      clientId: `condition-${Date.now()}`,
                      condition: '',
                      code: '',
                      onsetDate: null,
                      medicationIds: [],
                    })
                  }
                >
                  Add Condition
                </Button>

                <Divider className={styles.conditionDivider} />

                <div className={`${styles.sectionHeader} ${styles.medicationHeader}`}>
                  <Stack gap={2}>
                    <Title order={3}>Current Medications</Title>
                  </Stack>
                </div>

                <Table.ScrollContainer className={styles.medicationRows} minWidth={0}>
                  <Table className={styles.dataTable} verticalSpacing="md">
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Medication</Table.Th>
                        <Table.Th>Dosage</Table.Th>
                        <Table.Th>Frequency</Table.Th>
                        <Table.Th>Date Started</Table.Th>
                        <Table.Th>Date Stopped</Table.Th>
                        <Table.Th>N/A</Table.Th>
                        <Table.Th>Confirmation</Table.Th>
                        <Table.Th />
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {form.values.medications.length === 0 ? (
                        <Table.Tr>
                          <Table.Td colSpan={7}>
                            <Text c="dimmed">No medications added yet.</Text>
                          </Table.Td>
                        </Table.Tr>
                      ) : (
                        form.values.medications.map((row, index) => (
                          <Table.Tr key={row.clientId}>
                            <Table.Td>
                              <TextInput
                                disabled={isLocked}
                                placeholder="Medication"
                                value={row.medication}
                                onChange={(event) =>
                                  form.setFieldValue(`medications.${index}.medication`, event.currentTarget.value)
                                }
                              />
                            </Table.Td>
                            <Table.Td>
                              <TextInput
                                disabled={isLocked}
                                placeholder="Dosage"
                                value={row.dosage}
                                onChange={(event) =>
                                  form.setFieldValue(`medications.${index}.dosage`, event.currentTarget.value)
                                }
                              />
                            </Table.Td>
                            <Table.Td>
                              <Select
                                data={[
                                  ...MEDICATION_FREQUENCY_OPTIONS,
                                  { value: OTHER_FREQUENCY_VALUE, label: 'Other' },
                                  ...(!MEDICATION_FREQUENCY_OPTIONS.some((option) => option.value === row.frequency) &&
                                  row.frequency
                                    ? [{ value: row.frequency, label: row.frequency }]
                                    : []),
                                ]}
                                disabled={isLocked}
                                placeholder="Frequency"
                                value={row.frequencyOther ? OTHER_FREQUENCY_VALUE : row.frequency}
                                onChange={(value) => {
                                  form.setFieldValue(
                                    `medications.${index}.frequencyOther`,
                                    value === OTHER_FREQUENCY_VALUE
                                  );
                                  form.setFieldValue(
                                    `medications.${index}.frequency`,
                                    value === OTHER_FREQUENCY_VALUE ? '' : (value ?? '')
                                  );
                                }}
                              />
                              {row.frequencyOther && (
                                <TextInput
                                  mt="xs"
                                  disabled={isLocked}
                                  placeholder="Enter frequency"
                                  value={row.frequency}
                                  onChange={(event) =>
                                    form.setFieldValue(`medications.${index}.frequency`, event.currentTarget.value)
                                  }
                                  error={!row.frequency.trim() ? 'Frequency is required' : undefined}
                                />
                              )}
                            </Table.Td>
                            <Table.Td>
                              <TextInput
                                disabled={isLocked}
                                type="date"
                                value={row.dateStarted ?? ''}
                                onChange={(event) =>
                                  form.setFieldValue(
                                    `medications.${index}.dateStarted`,
                                    event.currentTarget.value || null
                                  )
                                }
                              />
                            </Table.Td>
                            <Table.Td>
                              <TextInput
                                disabled={isLocked || row.isDateStoppedNA}
                                type="date"
                                value={row.dateStopped ?? ''}
                                onChange={(event) =>
                                  form.setFieldValue(
                                    `medications.${index}.dateStopped`,
                                    event.currentTarget.value || null
                                  )
                                }
                              />
                            </Table.Td>
                            <Table.Td>
                              <Checkbox
                                checked={row.isDateStoppedNA}
                                disabled={isLocked}
                                onChange={(event) => {
                                  form.setFieldValue(
                                    `medications.${index}.isDateStoppedNA`,
                                    event.currentTarget.checked
                                  );
                                  if (event.currentTarget.checked) {
                                    form.setFieldValue(`medications.${index}.dateStopped`, null);
                                  }
                                }}
                              />
                            </Table.Td>
                            <Table.Td>
                              <Stack gap={2}>
                                <Text size="xs" c="dimmed">
                                  Source: {row.sourceDate ? formatDate(row.sourceDate) : 'chart'}
                                </Text>
                                {!row.confirmedAt && row.id ? (
                                  <Button
                                    size="compact-xs"
                                    variant="light"
                                    disabled={isLocked}
                                    onClick={() =>
                                      confirmClinicalRow('MedicationStatement', row.id as string).catch(
                                        showErrorNotification
                                      )
                                    }
                                  >
                                    Still current
                                  </Button>
                                ) : (
                                  <Badge size="xs" color="green">
                                    Confirmed
                                  </Badge>
                                )}
                              </Stack>
                            </Table.Td>
                            <Table.Td>
                              <ActionIcon
                                color="red"
                                disabled={isLocked}
                                onClick={() => form.removeListItem('medications', index)}
                              >
                                <IconTrash size={16} />
                              </ActionIcon>
                            </Table.Td>
                          </Table.Tr>
                        ))
                      )}
                    </Table.Tbody>
                  </Table>
                </Table.ScrollContainer>

                <Button
                  className={`${styles.inlineAdd} ${styles.medicationAdd}`}
                  variant="subtle"
                  leftSection={<IconPlus size={16} />}
                  disabled={isLocked}
                  onClick={() =>
                    form.insertListItem('medications', {
                      clientId: `medication-${Date.now()}`,
                      medication: '',
                      dosage: '',
                      frequency: '',
                      frequencyOther: false,
                      dateStarted: null,
                      dateStopped: null,
                      isDateStoppedNA: true,
                    })
                  }
                >
                  Add Medication
                </Button>
              </Stack>
            </Card>

            <Card withBorder radius="lg" p="lg">
              <div className={styles.sectionHeader}>
                <Title order={3}>Vitals</Title>
                <Text c="dimmed" size="sm">
                  Saved to the patient profile when the note is signed
                </Text>
              </div>
              <Grid gutter="md" mt="md">
                <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
                  <TextInput
                    label="Height"
                    placeholder="5′10″"
                    value={vitals.height}
                    disabled={isLocked}
                    onChange={(event) => setVitals({ ...vitals, height: event.currentTarget.value })}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
                  <TextInput
                    label="Weight (lb)"
                    type="number"
                    value={vitals.weight}
                    disabled={isLocked}
                    onChange={(event) => setVitals({ ...vitals, weight: event.currentTarget.value })}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
                  <TextInput label="BMI" value={bmi} placeholder="Calculated from height and weight" readOnly />
                  {bmi && (
                    <Text c="dimmed" size="xs" mt={4}>
                      Calculated from height and weight
                    </Text>
                  )}
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
                  <TextInput
                    label="Blood Pressure (mmHg)"
                    placeholder="120/80"
                    value={vitals.bloodPressure}
                    disabled={isLocked}
                    onChange={(event) => setVitals({ ...vitals, bloodPressure: event.currentTarget.value })}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
                  <TextInput
                    label="Heart Rate (bpm)"
                    type="number"
                    value={vitals.heartRate}
                    disabled={isLocked}
                    onChange={(event) => setVitals({ ...vitals, heartRate: event.currentTarget.value })}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
                  <TextInput
                    label="Respiratory Rate (/min)"
                    type="number"
                    value={vitals.respiratoryRate}
                    disabled={isLocked}
                    onChange={(event) => setVitals({ ...vitals, respiratoryRate: event.currentTarget.value })}
                  />
                </Grid.Col>
              </Grid>
            </Card>

            <Card withBorder radius="lg" p="lg">
              <Stack gap="lg">
                <div className={styles.sectionHeader}>
                  <Group gap="sm">
                    <Title order={3}>Diagnoses</Title>
                    <Badge variant="light">Encounter-linked conditions</Badge>
                  </Group>
                  <Button
                    variant="light"
                    leftSection={<IconPlus size={16} />}
                    disabled={isLocked}
                    onClick={() =>
                      form.insertListItem('diagnoses', {
                        concept: undefined,
                        code: '',
                        text: '',
                        recordedDate: new Date().toISOString().slice(0, 10),
                      })
                    }
                  >
                    Add Diagnosis
                  </Button>
                </div>

                <Stack gap="md">
                  {form.values.diagnoses.map((diagnosis, index) => {
                    const label = getDiagnosisLabel(index);
                    const codeLabel = `${label} Code`;
                    const diagnosisCoding = diagnosis.concept?.coding?.[0];

                    return (
                      <Grid
                        key={getDiagnosisInputKey(
                          diagnosis.id,
                          index,
                          diagnosisCoding?.code,
                          diagnosisCoding?.display ?? diagnosis.text
                        )}
                        gutter="md"
                      >
                        <Grid.Col span={{ base: 12, md: 5 }}>
                          <CodingInput
                            binding={ICD10_VALUESET}
                            disabled={isLocked}
                            label={label}
                            path="Condition.code"
                            defaultValue={diagnosisCoding}
                            name={`diagnoses.${index}`}
                            onChange={(coding) =>
                              form.setFieldValue(`diagnoses.${index}`, {
                                ...form.values.diagnoses[index],
                                code: coding?.code ?? '',
                                concept: buildIcd10Concept(coding ?? undefined),
                                text: coding?.display ?? '',
                              })
                            }
                          />
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, md: 5 }}>
                          <DateInput
                            clearable
                            disabled={isLocked}
                            label="Diagnosis Date"
                            value={diagnosis.recordedDate ?? null}
                            onChange={(date: string | null) =>
                              form.setFieldValue(`diagnoses.${index}.recordedDate`, date ?? undefined)
                            }
                          />
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, md: 5 }}>
                          <DiagnosisCodeInput
                            disabled={chartNoteStatus !== ChartNoteStatus.Unsigned}
                            label={codeLabel}
                            value={diagnosis.code}
                            onResolve={(coding) =>
                              form.setFieldValue(`diagnoses.${index}`, {
                                ...form.values.diagnoses[index],
                                code: coding.code ?? '',
                                concept: buildIcd10Concept(coding),
                                text: coding.display ?? coding.code ?? '',
                              })
                            }
                          />
                        </Grid.Col>
                        <Grid.Col className={styles.diagnosisRemove} span={{ base: 12, md: 2 }}>
                          <ActionIcon
                            aria-label={`Remove ${label}`}
                            color="red"
                            disabled={isLocked || form.values.diagnoses.length <= MIN_DIAGNOSIS_ENTRIES}
                            mt={30}
                            variant="subtle"
                            onClick={() => form.removeListItem('diagnoses', index)}
                          >
                            <IconTrash size={16} />
                          </ActionIcon>
                        </Grid.Col>
                      </Grid>
                    );
                  })}
                </Stack>

                <Stack gap={8}>
                  <Text fw={600} size="sm">
                    Suspected Positional Apnea
                  </Text>
                  <Radio.Group
                    value={form.values.suspectedPositionalApnea}
                    disabled={isLocked}
                    onChange={(value) => form.setFieldValue('suspectedPositionalApnea', value)}
                  >
                    <Group mt="xs">
                      <Radio value="yes" label="Yes" />
                      <Radio value="no" label="No" />
                    </Group>
                  </Radio.Group>
                </Stack>
              </Stack>
            </Card>

            <Card className={styles.notesSection} withBorder radius="lg" p="lg">
              <Stack gap="lg">
                <Title order={3}>Notes</Title>
                <Grid gutter="md">
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <TimeInput disabled={isLocked} label="Start Time" {...form.getInputProps('startTime')} />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <TimeInput disabled={isLocked} label="End Time" {...form.getInputProps('endTime')} />
                  </Grid.Col>
                </Grid>
                <Textarea
                  autosize
                  disabled={isLocked}
                  label="Treatment Plan"
                  minRows={3}
                  placeholder="Outline the treatment plan and next steps"
                  {...form.getInputProps('treatmentPlan')}
                />
                <PatientTreatmentsSection patient={patient} place="note" />
                <Textarea
                  autosize
                  disabled={isLocked}
                  label="Note for Patient"
                  minRows={3}
                  placeholder="Patient-facing note"
                  {...form.getInputProps('noteForPatient')}
                />
                <Textarea
                  autosize
                  disabled={isLocked}
                  label="Note for Referring Provider"
                  minRows={3}
                  placeholder="Referring-provider note"
                  {...form.getInputProps('noteForReferring')}
                />
              </Stack>
            </Card>

            <div className={styles.ordersSection}>
              <div className={styles.ordersGrid}>
                <div className={styles.orderColumn}>
                  <Title order={3}>Referrals</Title>
                  {referralRequests.map((referral) => (
                    <div
                      className={styles.orderItem}
                      key={referral.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => navigate(`/DmeOrder/${patient.id}?prescriptionId=${referral.id}&type=referral`)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          navigate(`/DmeOrder/${patient.id}?prescriptionId=${referral.id}&type=referral`);
                        }
                      }}
                    >
                      <Text className={styles.orderItemTitle}>{getOrderLabel(referral)}</Text>
                      <Text className={styles.orderItemSource}>Encounter referral</Text>
                      <Badge className={styles.orderStatus} variant="light">
                        {referral.status}
                      </Badge>
                    </div>
                  ))}
                  {referralRequests.length === 0 && legacyReferralRequests.length === 0 && (
                    <div className={styles.emptyState}>
                      <Text c="dimmed" size="sm">
                        No referrals linked to this encounter.
                      </Text>
                    </div>
                  )}
                  {legacyReferralRequests.map((request) => (
                    <div className={styles.referralCard} key={request.id}>
                      <Text className={styles.orderItemTitle}>{request.code?.text || 'Referral'}</Text>
                      <Text className={styles.orderItemSource}>
                        {request.performer?.[0]?.display ?? 'Referral destination'}
                      </Text>
                      <dl className={styles.referralMeta}>
                        <dt>Reason</dt>
                        <dd>{referralNoteValue(request, 'Reason:')}</dd>
                        <dt>Clinical notes</dt>
                        <dd>{referralNoteValue(request, 'Clinical notes:')}</dd>
                      </dl>
                      <div className={styles.referralOps}>
                        <button
                          type="button"
                          disabled={!isLocked}
                          onClick={() => {
                            if (request.id) {
                              navigate(`/Patient/${patient.id}/orders?orderId=${request.id}&action=download`);
                            }
                          }}
                        >
                          View referral form
                        </button>
                      </div>
                    </div>
                  ))}
                  <Button
                    className={styles.newOrderButton}
                    leftSection={<IconFilePlus size={16} />}
                    variant="subtle"
                    onClick={openReferral}
                    disabled={!canCreateReferral}
                    title={canCreateReferral ? undefined : 'Not available for this role'}
                  >
                    New Order
                  </Button>
                </div>

                <div className={styles.orderColumn}>
                  <Title order={3}>Tests</Title>
                  {serviceRequests.map((serviceRequest) => (
                    <div
                      className={styles.orderItem}
                      key={serviceRequest.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => setSelectedTest(serviceRequest)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          setSelectedTest(serviceRequest);
                        }
                      }}
                    >
                      <Text className={styles.orderItemTitle}>
                        {serviceRequest.code?.text || serviceRequest.code?.coding?.[0]?.display || 'Test'}
                      </Text>
                      <Text className={styles.orderItemSource}>Encounter order</Text>
                      <Badge className={styles.orderStatus} variant="light">
                        {serviceRequest.status}
                      </Badge>
                    </div>
                  ))}
                  {serviceRequests.length === 0 && (
                    <Text className={styles.orderEmpty}>No encounter-scoped tests yet.</Text>
                  )}
                  <Button
                    className={styles.newOrderButton}
                    leftSection={<IconFilePlus size={16} />}
                    disabled={isLocked}
                    variant="subtle"
                    onClick={openNewTest}
                  >
                    New Order
                  </Button>
                </div>

                <div className={styles.orderColumn}>
                  <Title order={3}>Prescriptions</Title>
                  {therapyRequests.map((medicationRequest) => (
                    <div
                      className={styles.orderItem}
                      key={medicationRequest.id}
                      role={isRpmOrder(medicationRequest) || isDmeOrder(medicationRequest) ? 'button' : undefined}
                      tabIndex={isRpmOrder(medicationRequest) || isDmeOrder(medicationRequest) ? 0 : undefined}
                      onClick={
                        isRpmOrder(medicationRequest) || isDmeOrder(medicationRequest)
                          ? () => openPrescriptionWindow(navigate, patient.id, encounter.id, medicationRequest)
                          : undefined
                      }
                      onKeyDown={
                        isRpmOrder(medicationRequest) || isDmeOrder(medicationRequest)
                          ? (event) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                openPrescriptionWindow(navigate, patient.id, encounter.id, medicationRequest);
                              }
                            }
                          : undefined
                      }
                    >
                      <Text className={styles.orderItemTitle}>{getOrderLabel(medicationRequest)}</Text>
                      <Text className={styles.orderItemSource}>
                        {medicationRequest.medicationCodeableConcept?.coding?.some((coding) => coding.code === 'rpm')
                          ? 'RPM enrollment · AirView'
                          : 'Encounter prescription'}
                      </Text>
                      <Badge className={styles.orderStatus} variant="light">
                        {medicationRequest.status}
                      </Badge>
                    </div>
                  ))}
                  {therapyRequests.length === 0 && (
                    <Text className={styles.orderEmpty}>No prescriptions ordered yet.</Text>
                  )}
                  <Button
                    className={styles.newOrderButton}
                    leftSection={<IconFilePlus size={16} />}
                    disabled={isLocked}
                    variant="subtle"
                    onClick={openNewPrescription}
                  >
                    New Therapy
                  </Button>
                  <Button
                    className={styles.newOrderButton}
                    leftSection={<IconFilePlus size={16} />}
                    disabled={isLocked}
                    variant="subtle"
                    onClick={() => openRpmOrderWindow(navigate, patient.id, encounter.id)}
                  >
                    RPM Order Form
                  </Button>
                  <Button
                    className={styles.newOrderButton}
                    leftSection={<IconExternalLink size={16} />}
                    disabled={isLocked}
                    variant="subtle"
                    onClick={openScriptSure}
                  >
                    Launch ScriptSure
                  </Button>
                </div>
              </div>
            </div>

            <div className={styles.billingSection}>
              <BillingTab
                patient={patient}
                encounter={encounter}
                setEncounter={(updatedEncounter) => setEncounter(updatedEncounter)}
                practitioner={practitioner}
                setPractitioner={(updatedPractitioner) => setPractitioner(updatedPractitioner)}
                chargeItems={chargeItems}
                setChargeItems={setChargeItems}
                claim={claim}
                setClaim={(updatedClaim) => setClaim(updatedClaim)}
                isLocked={isLocked}
              />
            </div>

            <div className={styles.footBar}>
              <Button variant="default" disabled>
                Delete Note
              </Button>
              <Button variant="default" disabled={isLocked} onClick={() => handleSaveDraft().catch(() => undefined)}>
                Save as Draft
              </Button>
              <Button color={BRAND} disabled={isLocked} onClick={() => handleOpenSign().catch(() => undefined)}>
                Sign &amp; Lock
              </Button>
            </div>

            {provenances.length > 0 && (
              <Card className={styles.signatureSection} withBorder radius="lg" p="lg">
                <Stack gap="lg">
                  <Title order={3}>Signature & Addenda</Title>
                  <Stack gap="sm">
                    {provenances.map((provenance, index) => (
                      <div className={styles.signatureRow} key={provenance.id ?? index}>
                        {index === provenances.length - 1 && isLocked ? (
                          <IconLock size={18} />
                        ) : (
                          <IconSignature size={18} />
                        )}
                        <Stack gap={2}>
                          <Text fw={600}>
                            {index === provenances.length - 1 && isLocked ? 'Signed and Locked' : 'Signed'} by{' '}
                            {provenance.agent?.[0]?.who?.display || 'Unknown Practitioner'}
                          </Text>
                          <Text c="dimmed" size="sm">
                            {formatDate(provenance.recorded)}
                          </Text>
                        </Stack>
                      </div>
                    ))}
                  </Stack>

                  {addendums.length > 0 && (
                    <Stack gap="sm">
                      {addendums.map((addendum, index) => (
                        <div className={styles.signatureRow} key={addendum.id ?? index}>
                          <IconWriting size={18} />
                          <Stack gap={2}>
                            <Text fw={600}>Addendum by {addendum.authorName}</Text>
                            <Text c="dimmed" size="sm">
                              {formatDate(addendum.timestamp)}
                            </Text>
                            <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                              {addendum.text}
                            </Text>
                          </Stack>
                        </div>
                      ))}
                    </Stack>
                  )}

                  <Textarea
                    autosize
                    label="Add Addendum"
                    minRows={3}
                    placeholder="Add an addendum to this visit"
                    value={addendumText}
                    onChange={(event) => setAddendumText(event.currentTarget.value)}
                  />
                  <Group justify="flex-end">
                    <Button
                      color={BRAND}
                      leftSection={<IconWriting size={16} />}
                      disabled={!addendumText.trim()}
                      onClick={() => handleAddAddendum().catch(() => undefined)}
                    >
                      Add Addendum
                    </Button>
                  </Group>
                </Stack>
              </Card>
            )}
          </Stack>
        </Card>
      </EncounterWorkspaceShell>
      <Modal opened={signOpened} onClose={closeSign} title="Signing As">
        <SignLockDialog
          onSign={(practitionerReference, lock) => {
            sign(practitionerReference, lock)
              .catch((error: unknown) => {
                Sentry.captureException(error);
                showErrorNotification(error);
              })
              .finally(closeSign);
          }}
        />
      </Modal>

      <OrderNewTestModal
        opened={newTestOpened}
        onClose={handleCloseNewTest}
        encounter={encounter}
        patient={patient}
        practitioner={practitioner}
      />
      <Modal
        opened={selectedTest !== undefined}
        onClose={() => setSelectedTest(undefined)}
        title={selectedTest?.code?.text || 'Test Details'}
        centered
      >
        {selectedTest && (
          <Stack gap="sm">
            <Text>
              <strong>Status:</strong> {selectedTest.status}
            </Text>
            <Text>
              <strong>Order date:</strong>{' '}
              {selectedTest.occurrenceDateTime ? formatDate(selectedTest.occurrenceDateTime) : 'Not recorded'}
            </Text>
            {selectedTest.code?.coding?.map((coding) => (
              <Text key={`${coding.system ?? 'code'}-${coding.code ?? coding.display}`}>
                <strong>{coding.system?.includes('cpt') ? 'CPT' : 'Code'}:</strong>{' '}
                {coding.code ?? coding.display ?? 'Not recorded'}
              </Text>
            ))}
            {selectedTest.note?.map((note, index) => (
              <Text key={`${note.text ?? 'note'}-${index}`}>
                <strong>Note:</strong> {note.text}
              </Text>
            ))}
            <Button variant="default" onClick={() => setSelectedTest(undefined)}>
              Close
            </Button>
          </Stack>
        )}
      </Modal>
      <NewPrescriptionModal
        opened={newPrescriptionOpened}
        onClose={closeNewPrescription}
        onOrdered={() => refreshOrders().catch(showErrorNotification)}
        patient={patient}
        encounter={encounter}
      />
      <ReferralModal
        opened={referralOpened}
        onClose={handleCloseReferral}
        onCreated={() => refreshOrders().catch(showErrorNotification)}
        patient={patient}
        encounter={encounter}
        encounterDiagnoses={referralNoteDiagnoses}
      />
      <Modal
        opened={scriptSureOpened}
        onClose={closeScriptSure}
        size="95%"
        centered
        withCloseButton={false}
        closeOnClickOutside={false}
        closeOnEscape={false}
        styles={{
          content: {
            height: 'calc(100vh - 32px)',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
          },
          body: { flex: 1, minHeight: 0, overflow: 'hidden', padding: 0 },
        }}
      >
        <ActionIcon
          aria-label="Close ScriptSure prescribing"
          variant="subtle"
          color="gray"
          onClick={closeScriptSure}
          style={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}
        >
          <IconX size={20} />
        </ActionIcon>
        <ScriptSureIFrame
          patientId={patient.id}
          title="ScriptSure prescribing"
          loadingLabel="Preparing ScriptSure prescribing…"
          iframeHeight="100%"
        />
      </Modal>
    </>
  );
}
